/**
 * VTodoCreator.ts
 *
 * VTodo 생성을 담당하는 모듈 (ICalendar - VTodo)
 *
 * 주요 기능:
 * - TodoItem을 ICS 형식의 VTODO로 변환
 * - VTODO 문자열 생성
 * - VALARM 생성
 */
import moment from 'moment-timezone'
import { RRule } from 'rrule'
import { generateVTimezone } from 'pages/calendar/modules/ICSGenerator'
import { convertToRRuleOptions } from 'pages/calendar/utils/rruleCompat'
import { TaskReminder, TodoItem } from './TodoItem'
const CRLF = '\r\n'

export interface VTodoOptions {
  method?:
    | 'PUBLISH'
    | 'REQUEST'
    | 'REPLY'
    | 'ADD'
    | 'CANCEL'
    | 'REFRESH'
    | 'COUNTER'
    | 'DECLINECOUNTER'
  comment?: string
}

/**
 * TodoItem 객체를 ICS 형식의 VTODO 문자열로 변환합니다.
 * @param todo - TodoItem 객체
 * @returns ICS 형식의 문자열
 */
export function generateVTodoICS(todo: TodoItem, options?: VTodoOptions): string {
  // alarms 존재 여부에 따라 datetype 동적 설정
  const hasAlarms = todo.alarms && todo.alarms.length > 0
  const modifiedTodo = {
    ...todo,
    datetype: hasAlarms ? 'datetime' : 'date',
  }

  // 타임존 정보 수집
  const timezones = new Set<string>()

  // startDate와 dueDate가 datetime이고 타임존 정보가 있는 경우
  const isDateOnly = modifiedTodo.datetype === 'date'
  if (!isDateOnly) {
    if (modifiedTodo.startDate) {
      const startTz = moment.tz.guess() // 현재 사용되는 타임존
      timezones.add(startTz)
    }
    if (modifiedTodo.dueDate) {
      const dueTz = moment.tz.guess() // 현재 사용되는 타임존
      timezones.add(dueTz)
    }
  }

  const todoString = _todoToICSString(modifiedTodo, options?.comment, timezones)

  // VTIMEZONE 문자열 생성
  let tzStrings: string[] = []
  timezones.forEach((tzName) => {
    const tzString = generateVTimezone(tzName)
    if (tzString) {
      tzStrings.push(tzString.trim()) // 마지막 CRLF 제거
    }
  })

  let returnString = 'BEGIN:VCALENDAR' + CRLF
  returnString += 'VERSION:2.0' + CRLF
  returnString += 'PRODID:-//JiranSoft//OfficeMail OWA//EN' + CRLF

  // VTIMEZONE 컴포넌트 추가
  if (tzStrings.length > 0) {
    returnString += tzStrings.join(CRLF) + CRLF
  }

  returnString += todoString
  returnString += CRLF + 'END:VCALENDAR' + CRLF

  return returnString
}

const encodeValue = (value: string | null | undefined): string => {
  if (!value) {
    return ''
  }
  return value.replace(/\\/g, '\\\\').replace(/\n/g, '\\n')
}

function _todoToICSString(todo: TodoItem, comment?: string, timezones?: Set<string>): string {
  let vTodoStrings = 'BEGIN:VTODO' + CRLF

  // Required properties
  // UID (required)
  vTodoStrings += 'UID:' + (todo.uid || '') + CRLF

  // CalDAV 및 iCalendar 명세(RFC 5545)에서는 DTSTART와 DUE가 둘 다 DATE-TIME 또는 둘 다 DATE로 일치해야 함.

  // DTSTAMP (required)
  let dtstamp = todo.dtstamp
  if (!dtstamp) {
    dtstamp = new Date()
  }
  vTodoStrings += 'DTSTAMP:' + moment(dtstamp).utc().format('YYYYMMDDTHHmmss') + 'Z' + CRLF

  // CREATED
  if (todo.createdDate) {
    vTodoStrings +=
      'CREATED:' + moment(todo.createdDate).utc().format('YYYYMMDDTHHmmss') + 'Z' + CRLF
  }

  // Optional properties
  // SUMMARY
  const summary = encodeValue(todo.summary)
  vTodoStrings += 'SUMMARY:' + summary + CRLF

  // DESCRIPTION
  if (todo.description) {
    const description = encodeValue(todo.description)
    vTodoStrings += 'DESCRIPTION:' + description + CRLF
  }

  // STATUS
  if (todo.status) {
    vTodoStrings += 'STATUS:' + todo.status + CRLF
  }

  // PRIORITY
  if (todo.priority) {
    const priorityNum = priorityToPriorityNumber(todo.priority)
    vTodoStrings += 'PRIORITY:' + priorityNum + CRLF
  }

  // PERCENT-COMPLETE
  if (todo.percentComplete !== undefined) {
    vTodoStrings += 'PERCENT-COMPLETE:' + todo.percentComplete + CRLF
  }

  // RFC 5545: DTSTART와 DUE는 동일한 값 유형(DATE 또는 DATE-TIME)이어야 함
  // datetype에 따라 둘 다 DATE 또는 둘 다 DATE-TIME으로 통일
  const isDateOnly = todo.datetype === 'date'

  // DTSTART
  if (todo.startDate) {
    if (isDateOnly) {
      vTodoStrings += 'DTSTART;VALUE=DATE:' + moment(todo.startDate).format('YYYYMMDD') + CRLF
    } else {
      const startTz = moment.tz.guess() // 기본 타임존 사용
      vTodoStrings += `DTSTART;TZID=${startTz}:${moment(todo.startDate).tz(startTz).format('YYYYMMDDTHHmmss')}${CRLF}`
    }
  }

  // DUE - DTSTART와 동일한 값 유형 사용
  if (todo.dueDate) {
    if (isDateOnly) {
      vTodoStrings += 'DUE;VALUE=DATE:' + moment(todo.dueDate).format('YYYYMMDD') + CRLF
    } else {
      const dueTz = moment.tz.guess() // 기본 타임존 사용
      vTodoStrings += `DUE;TZID=${dueTz}:${moment(todo.dueDate).tz(dueTz).format('YYYYMMDDTHHmmss')}${CRLF}`
    }
  }

  // LOCATION ????? 사용 하려나???
  if (todo.location) {
    const location = encodeValue(todo.location)
    vTodoStrings += 'LOCATION:' + location + CRLF
  }

  // COMPLETED
  if (todo.completedDate) {
    // completedDate가 string 또는 Date 객체 모두 처리 가능
    vTodoStrings +=
      'COMPLETED:' + moment(todo.completedDate).utc().format('YYYYMMDDTHHmmss') + 'Z' + CRLF
  }

  // RRULE (recurrence rule)
  // TODO : recurrenceRule type 에 따라 처리 필요.
  let rrulStr = null
  if (todo.recurrenceRule) {
    var rrule = todo.recurrenceRule //new RRule(todo.recurrenceRule)
    console.log('rrule', rrule)

    let untilStr = null
    // RRULE 객체에서 until 속성이 있는 경우. 변환시 Z를 붙이지 않아 오류 발생하므로 별도 처리
    if (rrule.origOptions.until) {
      // node-ical(rrule-temporal)에서 파싱된 RRule의 origOptions는 camelCase(byDay 등)와
      // exDate/byHour 같은 추가 키를 포함해 그대로 new RRule(...)에 넘기면 throw 한다.
      const origOptions = convertToRRuleOptions(
        rrule.origOptions as unknown as Record<string, unknown>,
      )
      const backupUntil = origOptions.until
      delete origOptions.until
      rrule = new RRule(origOptions)

      // datetype이 'date'인 경우 DATE 형식으로, 아니면 DATE-TIME 형식으로 처리
      if (isDateOnly) {
        untilStr = moment(backupUntil).format('YYYYMMDD')
      } else {
        untilStr = moment(backupUntil).utc().format('YYYYMMDDTHHmmss') + 'Z'
      }
    }
    const rrulStrArr = rrule.toString().split('\n')
    rrulStr = rrulStrArr.find((str) => str.startsWith('RRULE:'))
    if (untilStr) {
      rrulStr += `;UNTIL=${untilStr}`
    }
  }

  if (rrulStr) {
    vTodoStrings += rrulStr + CRLF
  }

  // RECURRENCE-ID (완료된 반복 인스턴스 식별)
  if (todo.recurrenceId) {
    const currentTz = moment.tz.guess()
    vTodoStrings += `RECURRENCE-ID;TZID=${currentTz}:${moment(todo.recurrenceId).tz(currentTz).format('YYYYMMDDTHHmmss')}${CRLF}`
  }

  // COMMENT
  if (comment) {
    vTodoStrings += 'COMMENT:' + encodeValue(comment) + CRLF
  }

  // VALARM (alarms/reminders)
  if (todo.alarms && todo.alarms.length > 0) {
    todo.alarms.forEach((alarm) => {
      vTodoStrings += generateVAlarm(alarm, timezones)
    })
  }

  if (todo.lastModifiedDate) {
    vTodoStrings +=
      'LAST-MODIFIED:' + moment(todo.lastModifiedDate).utc().format('YYYYMMDDTHHmmss') + 'Z' + CRLF
  }

  if (todo.author) {
    vTodoStrings += 'X-NI-AUTHOR;CN=' + todo.author.params.CN + ':' + todo.author.val + CRLF
  }

  // Unknown properties - 우리가 사용 못한 filed 들은 그대로 올려주자.
  if (todo.unknownProperties && todo.unknownProperties.length > 0) {
    todo.unknownProperties.forEach((prop) => {
      // dueDate가 있고, key가 'end'이면 무시
      // key가 'params' 또는 'datetype'인 경우도 무시
      if ((todo.dueDate && prop.key === 'end') || prop.key === 'params' || prop.key === 'datetype')
        return

      // "X-"로 시작하는 속성만 추가
      if (prop.key.startsWith('X-')) {
        vTodoStrings += `${prop.key}:${prop.value}${CRLF}`
      }
    })
  }

  vTodoStrings += 'END:VTODO'

  return vTodoStrings
}

function generateVAlarm(alarm: TaskReminder, timezones?: Set<string>): string {
  let alarmString = `BEGIN:VALARM${CRLF}`

  // ACTION (required for VALARM)
  alarmString += `ACTION:${alarm.action || 'DISPLAY'}${CRLF}`

  // TRIGGER (required for VALARM)
  if (typeof alarm.trigger === 'string') {
    alarmString += `TRIGGER:${alarm.trigger}${CRLF}`
  } else if (alarm.trigger && typeof alarm.trigger === 'object' && alarm.trigger.val) {
    // 절대 시간 트리거 - VALUE=DATE-TIME 사용
    const params = alarm.trigger.params || {}
    let paramStr = Object.keys(params).length > 0 ? '' : ';VALUE=DATE-TIME'
    Object.entries(params).forEach(([key, value]) => {
      paramStr += `;${key}=${value}`
    })
    alarmString += `TRIGGER${paramStr}:${alarm.trigger.val}${CRLF}`

    // 절대 시간 트리거가 TZID를 사용하는 경우 타임존 정보 수집
    if (params.TZID && timezones) {
      timezones.add(params.TZID)
    }
  }

  // DESCRIPTION (required for DISPLAY action)
  if (alarm.action === 'DISPLAY' || !alarm.action) {
    alarmString += `DESCRIPTION:Task Reminder${CRLF}`
  }

  alarmString += `END:VALARM${CRLF}`

  return alarmString
}

// Priority 타입을 숫자로 변환
function priorityToPriorityNumber(priority: string): string {
  switch (priority) {
    case 'HIGH':
      return '1'
    case 'MEDIUM':
      return '5'
    case 'LOW':
      return '9'
    default:
      return '5'
  }
}
